import { DatePicker, Form, notification, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { useEffect } from "react";

import "./SalesTargetTimePeriods.scss";
import { UtilityPrograms } from "projects/constants";
import { useBoundStore } from "store";
import { useUpdateOrganizationUserMetadata } from "users/queries";

dayjs.extend(quarterOfYear);

type Props = {
  name: string;
  onChange?: () => void;
};

interface Values {
  salesTargetYear: Dayjs;
  salesTargetMonth: Dayjs;
  salesTargetQuarter: Dayjs;
  utilityProgram: string;
}

export const SalesTargetTimePeriods = ({ name, onChange }: Props) => {
  const [form] = Form.useForm();
  const { currentOrganization, user } = useBoundStore();
  const userId = user?.userId || user?.auth0UserId;
  const organizationId = currentOrganization?.id;
  const { mutateAsync: updateMetadata, error, isSuccess } = useUpdateOrganizationUserMetadata(
    userId,
    organizationId
  );
  const [api, notificationContextHolder] = notification.useNotification();

  const onChangeHandler = (
    _: Values,
    {
      salesTargetYear,
      salesTargetMonth,
      salesTargetQuarter,
      utilityProgram,
    }: Values
  ) => {
    const year = salesTargetYear?.year() ?? null;
    const month = salesTargetMonth?.month() ?? null;
    const quarter = salesTargetQuarter?.quarter() ?? null;
    const utility = utilityProgram ?? null;

    updateMetadata({
      ...(user?.metadata ?? {}),
      salesTargetYear: year ?? undefined,
      salesTargetMonth: month !== null ? month + 1 : undefined,
      salesTargetQuarter: quarter ?? undefined,
      utilityProgram: utility ?? undefined,
    });
  };

  useEffect(() => {
    if (!user?.metadata) return;

    const { salesTargetMonth, salesTargetQuarter, salesTargetYear, utilityProgram } = user.metadata;

    if (salesTargetMonth === undefined) {
      form.setFieldsValue({ salesTargetMonth: undefined });
    }

    form.setFieldsValue({
      salesTargetYear:
        salesTargetYear !== undefined ? dayjs().year(salesTargetYear ?? 0) : undefined,
      salesTargetQuarter:
        salesTargetQuarter !== undefined ? dayjs().quarter(salesTargetQuarter ?? 0) : undefined,
      salesTargetMonth:
        salesTargetMonth !== undefined ? dayjs().month((salesTargetMonth ?? 1) - 1) : undefined,
      utilityProgram,
    });
  }, [form, user]);

  useEffect(() => {
    if (isSuccess && onChange) {
      onChange();
    } else if (error) {
      api.error({
        message: "Update user sales target error",
        description: error?.message,
      });
    }
  }, [isSuccess, error, api, onChange]);

  return (
    <>
      {notificationContextHolder}
      <Form
        form={form}
        name={`${name}_form`}
        data-testid='salesTargetTimePeriods'
        onValuesChange={onChangeHandler}
        requiredMark={false}
        layout='inline'
        initialValues={{
          salesTargetYear:
          user?.metadata?.salesTargetYear !== undefined
              ? dayjs().year(user?.metadata?.salesTargetYear ?? 0)
              : undefined,
          salesTargetMonth:
          user?.metadata?.salesTargetMonth !== undefined
              ? dayjs().month((user?.metadata?.salesTargetMonth ?? 1) - 1)
              : undefined,
          salesTargetQuarter:
          user?.metadata?.salesTargetQuarter !== undefined
              ? dayjs().quarter(user?.metadata?.salesTargetQuarter ?? 0)
              : undefined,
          utilityProgram: user?.metadata?.utilityProgram,
        }}
      >
        <Form.Item
          name='salesTargetYear'
          data-testid={`${name}_salesTargetYear_testid`}
          className='sales-target-time-periods-form-item'
        >
          <DatePicker
            placeholder='Year'
            mode='year'
            picker='year'
            allowClear
            size='small'
            variant='borderless'
            minDate={dayjs("1970-01-01")}
          />
        </Form.Item>
        <Form.Item
          name='salesTargetQuarter'
          data-testid={`${name}_salesTargetQuarter_testid`}
          className='sales-target-time-periods-form-item'
        >
          <DatePicker
            placeholder='Quarter'
            mode='quarter'
            picker='quarter'
            allowClear
            size='small'
            variant='borderless'
            format={(value) => `Q${value.format("Q")}`}
          />
        </Form.Item>
        <Form.Item
          name='salesTargetMonth'
          data-testid={`${name}_salesTargetMonth_testid`}
          className='sales-target-time-periods-form-item'
        >
          <DatePicker
            placeholder='Month'
            mode='month'
            picker='month'
            allowClear
            size='small'
            variant='borderless'
            format='MMMM'
            disabledDate={(current) => {
              const quarter = form.getFieldValue("salesTargetQuarter");
              if (quarter) {
                const startMonth = (quarter.quarter() - 1) * 3;
                const endMonth = startMonth + 2;
                return (
                  current.month() < startMonth || current.month() > endMonth
                );
              }
              return false;
            }}
          />
        </Form.Item>
        <Form.Item
          name='utilityProgram'
          data-testid={`${name}_utilityProgram_testid`}
          className='sales-target-time-periods-form-item'
        >
          <Select
            allowClear
            placeholder='Utility Program'
            size='small'
            variant='borderless'
            options={UtilityPrograms}
            onSelect={(value) => {
              form.setFieldValue("utilityProgram", value);
            }}
          />
        </Form.Item>
      </Form>
    </>
  );
};
